import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Activity,
  ArrowDownToLine,
  Coins,
  Loader2,
  PauseCircle,
  PlayCircle,
  Users,
  Wallet,
} from "lucide-react";
import BondDrawInvestModal from "../bond-draw/BondDrawInvestModal";
import BondDrawCashoutModal from "../bond-draw/BondDrawCashoutModal";
import BondPlayConfirmModal from "../bond-play/BondPlayConfirmModal";
import BondPlayPlayerCard from "../bond-coin/BondPlayPlayerCard";
import BondPlayInvestorCard from "../bond-coin/BondPlayInvestorCard";
import {
  coinFlipInvest,
  coinFlipInvestorCashout,
  coinFlipInvestorToggleActive,
  fetchCoinFlipInvestors,
  fetchCoinFlipPlayers,
  fetchCoinFlipPool,
  selectCoinFlipInvestorActionLoading,
  selectCoinFlipInvestors,
  selectCoinFlipLoadingInvestors,
  selectCoinFlipLoadingPlayers,
  selectCoinFlipPlayers,
  selectCoinFlipPool,
} from "../../redux/coinFlipSlice";
import { formatCoinFlipCoin } from "../../utils/coinFlipHelpers";

const BondCoinFlipSidePanel = ({ userCoin = 0, onNotify }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("players");
  const [showInvest, setShowInvest] = useState(false);
  const [showCashout, setShowCashout] = useState(false);
  const [toggleConfirm, setToggleConfirm] = useState(null);

  const pool = useSelector(selectCoinFlipPool);
  const players = useSelector(selectCoinFlipPlayers);
  const investors = useSelector(selectCoinFlipInvestors);
  const loadingPlayers = useSelector(selectCoinFlipLoadingPlayers);
  const loadingInvestors = useSelector(selectCoinFlipLoadingInvestors);
  const actionLoading = useSelector(selectCoinFlipInvestorActionLoading);
  const currentUserId = useSelector((state) => state.appAuth?.user?._id);

  useEffect(() => {
    dispatch(fetchCoinFlipPool());
    dispatch(fetchCoinFlipPlayers({ limit: 100 }));
    dispatch(fetchCoinFlipInvestors({ limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (activeTab === "players") dispatch(fetchCoinFlipPlayers({ limit: 100 }));
    if (activeTab === "investors") dispatch(fetchCoinFlipInvestors({ limit: 100 }));
  }, [activeTab, dispatch]);

  const sortedPlayers = useMemo(() => {
    const arr = [...(players || [])];
    arr.sort((a, b) => {
      if (currentUserId) {
        const aMine = String(a.userId) === String(currentUserId);
        const bMine = String(b.userId) === String(currentUserId);
        if (aMine && !bMine) return -1;
        if (!aMine && bMine) return 1;
      }
      return b.won + b.pending - b.lost - (a.won + a.pending - a.lost);
    });
    return arr;
  }, [players, currentUserId]);

  const sortedInvestors = useMemo(() => {
    const arr = [...(investors || [])];
    arr.sort((a, b) => {
      if (currentUserId) {
        const aMine = String(a.userId) === String(currentUserId);
        const bMine = String(b.userId) === String(currentUserId);
        if (aMine && !bMine) return -1;
        if (!aMine && bMine) return 1;
      }
      return (b.active || 0) - (a.active || 0);
    });
    return arr;
  }, [investors, currentUserId]);

  const currentInvestor = useMemo(() => {
    if (!currentUserId) return null;
    return sortedInvestors.find((inv) => String(inv.userId) === String(currentUserId)) || null;
  }, [sortedInvestors, currentUserId]);

  const refresh = () => {
    dispatch(fetchCoinFlipPool());
    dispatch(fetchCoinFlipPlayers({ limit: 100 }));
    dispatch(fetchCoinFlipInvestors({ limit: 100 }));
  };

  const handleInvest = async (amount) => {
    const result = await dispatch(coinFlipInvest({ amount }));
    if (coinFlipInvest.fulfilled.match(result)) {
      setShowInvest(false);
      refresh();
      onNotify?.({
        variant: "invest",
        game: "flip",
        title: currentInvestor ? "Balance added" : "Welcome to the pool",
        subtitle: "Your COIN now backs instant coin flips.",
        amount,
        autoCloseMs: 4000,
      });
    } else {
      onNotify?.(result.payload || "Invest failed.");
    }
  };

  const handleCashout = async (amount) => {
    const result = await dispatch(coinFlipInvestorCashout({ amount }));
    if (coinFlipInvestorCashout.fulfilled.match(result)) {
      setShowCashout(false);
      refresh();
      onNotify?.({
        variant: "cashout",
        game: "flip",
        title: "Cash out complete",
        subtitle: "Funds returned to your COIN balance.",
        amount,
        autoCloseMs: 4000,
      });
    } else {
      onNotify?.(result.payload || "Cash out failed.");
    }
  };

  const handleToggle = async () => {
    const nextActive = toggleConfirm?.nextActive;
    if (typeof nextActive !== "boolean") return;
    const result = await dispatch(coinFlipInvestorToggleActive({ isActive: nextActive }));
    if (coinFlipInvestorToggleActive.fulfilled.match(result)) {
      setToggleConfirm(null);
      refresh();
      onNotify?.({
        variant: "info",
        game: "flip",
        title: nextActive ? "Investor activated" : "Investor paused",
        subtitle: nextActive
          ? "Your pool share will back new flips."
          : "Your balance stays safe — no new reservations.",
        autoCloseMs: 3500,
      });
    } else {
      onNotify?.(result.payload || "Could not update status.");
    }
  };

  const activeTotal = Number(pool?.activeTotal || 0);
  const reserved = Number(pool?.reservedTotal || 0);
  const total = activeTotal + reserved;
  const activePct = total > 0 ? (activeTotal / total) * 100 : 0;
  const investorsCount = Number(pool?.investorsCount || sortedInvestors.length);
  const activeInvestors = Number(pool?.activeInvestors || 0);
  const investorActive = Number(currentInvestor?.active || 0);
  const isActive = !!currentInvestor?.activeStatus;

  const renderTab = (key, label, icon, count) => {
    const isTabActive = activeTab === key;
    return (
      <button
        key={key}
        type="button"
        className={`bdraw-tab${isTabActive ? " active" : ""}`}
        onClick={() => setActiveTab(key)}
        aria-selected={isTabActive}
        role="tab"
      >
        {icon}
        <span>{label}</span>
        <span className="bdraw-tab-count">{count}</span>
      </button>
    );
  };

  const renderPlayers = () => {
    if (loadingPlayers && sortedPlayers.length === 0) {
      return (
        <div className="bdraw-panel-loading">
          <Loader2 size={18} className="bdraw-spin" />
          <span>Loading players…</span>
        </div>
      );
    }
    if (sortedPlayers.length === 0) {
      return (
        <div className="bdraw-panel-empty">
          <Users size={22} strokeWidth={2} />
          <p>No players yet</p>
          <span>Be the first to reserve a coin flip ticket.</span>
        </div>
      );
    }
    return (
      <ul className="bdraw-panel-list bdraw-player-list">
        {sortedPlayers.map((player) => (
          <BondPlayPlayerCard
            key={player.userId}
            player={player}
            game="flip"
            isCurrentUser={currentUserId && String(player.userId) === String(currentUserId)}
          />
        ))}
      </ul>
    );
  };

  const renderInvestorTabActions = () => (
    <div className="bdraw-panel-invest-actions">
      <button type="button" className="bdraw-panel-invest-btn primary" onClick={() => setShowInvest(true)}>
        <Wallet size={13} strokeWidth={2.5} />
        <span>{currentInvestor ? "Add balance" : "Join pool"}</span>
      </button>
      {currentInvestor ? (
        <div className="bdraw-panel-invest-row">
          <button
            type="button"
            className="bdraw-panel-invest-btn cashout"
            disabled={actionLoading || investorActive <= 0}
            onClick={() => setShowCashout(true)}
          >
            <ArrowDownToLine size={13} strokeWidth={2.5} />
            <span>Cash out</span>
            {investorActive > 0 ? <em>{formatCoinFlipCoin(investorActive)}</em> : null}
          </button>
          <button
            type="button"
            className={`bdraw-panel-invest-btn ${isActive ? "deactivate" : "activate"}`}
            disabled={actionLoading}
            onClick={() =>
              setToggleConfirm({
                nextActive: !isActive,
                title: isActive ? "Deactivate investor account?" : "Activate investor account?",
                confirmLabel: isActive ? "Deactivate" : "Activate",
                tone: isActive ? "danger" : "primary",
              })
            }
          >
            {isActive ? <PauseCircle size={13} strokeWidth={2.5} /> : <PlayCircle size={13} strokeWidth={2.5} />}
            <span>{isActive ? "Deactivate" : "Activate"}</span>
          </button>
        </div>
      ) : null}
    </div>
  );

  const renderInvestors = () => (
    <>
      {renderInvestorTabActions()}
      {loadingInvestors && sortedInvestors.length === 0 ? (
        <div className="bdraw-panel-loading">
          <Loader2 size={18} className="bdraw-spin" />
          <span>Loading investors…</span>
        </div>
      ) : sortedInvestors.length === 0 ? (
        <div className="bdraw-panel-empty">
          <Wallet size={22} strokeWidth={2} />
          <p>No investors yet</p>
          <span>Investors fund the pool that backs each flip.</span>
        </div>
      ) : (
        <ul className="bdraw-panel-list bdraw-player-list">
          {sortedInvestors.map((inv) => (
            <BondPlayInvestorCard
              key={inv.userId}
              investor={inv}
              game="flip"
              isCurrentUser={currentUserId && String(inv.userId) === String(currentUserId)}
            />
          ))}
        </ul>
      )}
    </>
  );

  return (
    <>
      <aside className="bplay-games-rail bcoin-side-rail bdraw-side" aria-label="Coin flip activity">
        <div className="bcoin-side-rail-scroll">
          <div className="bcoin-pool-card">
            <div className="bcoin-pool-card-head">
              <span className="bcoin-pool-kicker">
                <Activity size={11} strokeWidth={2.5} />
                Investor pool
              </span>
              <span className="bcoin-pool-badge">
                {activeInvestors}/{investorsCount} active
              </span>
            </div>
            <strong className="bcoin-pool-amount">
              {formatCoinFlipCoin(activeTotal)} <em>COIN</em>
            </strong>
            <div className="bcoin-pool-bar">
              <span className="bcoin-pool-bar-active" style={{ width: `${activePct}%` }} />
              <span className="bcoin-pool-bar-reserved" style={{ width: `${100 - activePct}%` }} />
            </div>
            <div className="bcoin-pool-meta">
              <span>
                <span className="bcoin-pool-dot active" /> {formatCoinFlipCoin(activeTotal)} active
              </span>
              <span>
                <span className="bcoin-pool-dot reserved" /> {formatCoinFlipCoin(reserved)} reserved
              </span>
            </div>
          </div>

          <div className="bactivity-ledger-head bcoin-activity-ledger-head">
            <span>Activity ledger</span>
            <strong>Players & investors</strong>
          </div>

          <div className="bdraw-tabs" role="tablist">
            {renderTab("players", "Players", <Users size={12} strokeWidth={2.5} />, sortedPlayers.length)}
            {renderTab("investors", "Investors", <Coins size={12} strokeWidth={2.5} />, sortedInvestors.length)}
          </div>

          <div className="bdraw-panel-body" role="tabpanel">
            {activeTab === "players" ? renderPlayers() : renderInvestors()}
          </div>
        </div>
      </aside>

      <BondDrawInvestModal
        open={showInvest}
        maxAmount={userCoin}
        loading={actionLoading}
        isExistingInvestor={!!currentInvestor}
        onClose={() => !actionLoading && setShowInvest(false)}
        onSubmit={handleInvest}
      />

      {currentInvestor ? (
        <BondDrawCashoutModal
          open={showCashout}
          maxAmount={investorActive}
          loading={actionLoading}
          onClose={() => !actionLoading && setShowCashout(false)}
          onSubmit={handleCashout}
        />
      ) : null}

      <BondPlayConfirmModal
        open={!!toggleConfirm}
        eyebrow="Investor account"
        title={toggleConfirm?.title}
        description={
          <p>
            Your flip pool balance will {toggleConfirm?.nextActive ? "join" : "leave"} new
            reservations.
          </p>
        }
        confirmLabel={toggleConfirm?.confirmLabel || "Confirm"}
        tone={toggleConfirm?.tone || "primary"}
        loading={actionLoading}
        onClose={() => !actionLoading && setToggleConfirm(null)}
        onConfirm={handleToggle}
      />
    </>
  );
};

export default BondCoinFlipSidePanel;
